// components/HowToReach.jsx
import React from "react";

// --- 1. Internal Icon Components ---
// Kept outside to keep the main component clean
const Icons = {
    Plane: ({ className }) => (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
            <path d="M2 12h20" />
            <path d="M13 2l9 10-9 10" />
            <path d="M12 2l3 10-3 10" />
        </svg>
    ),
    Car: ({ className }) => (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
            <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-1.66-1.34-3-3-3H5c-1.66 0-3 1.34-3 3v3c0 .6.4 1 1 1h2" />
            <path d="M14 17H9" />
            <circle cx="7" cy="17" r="2" />
            <circle cx="17" cy="17" r="2" />
            <path d="M5 10l2-5h10l2 5" />
        </svg>
    ),
    Train: ({ className }) => (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
            <rect width="16" height="16" x="4" y="3" rx="2" />
            <path d="M4 11h16" />
            <path d="M12 3v8" />
            <path d="M8 19l-2 3" />
            <path d="M18 22l-2-3" />
            <path d="M8 15h0" />
            <path d="M16 15h0" />
        </svg>
    ),
    Bus: ({ className }) => (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
            <path d="M8 6v6" />
            <path d="M15 6v6" />
            <path d="M2 12h19.6" />
            <path d="M18 18h3s.5-1.7.8-2.8c.1-.4.2-.8.2-1.2 0-.4-.1-.8-.2-1.2l-1.4-5C20.1 6.8 19.1 6 18 6H4a2 2 0 0 0-2 2v10h3" />
            <circle cx="7" cy="18" r="2" />
            <circle cx="17" cy="18" r="2" />
        </svg>
    ),
    MapPin: ({ className }) => (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
            <circle cx="12" cy="10" r="3" />
        </svg>
    ),
};

// --- 2. Data Definition ---
const SECTIONS_DATA = [
    {
        id: "air",
        label: "By Air",
        mainIcon: <Icons.Plane className="w-6 h-6" />,
        description: "Fastest route for international & distant travelers",
        blocks: [
            {
                title: "Chennai International Airport (MAA)",
                badge: "Nearest Airport",
                icon: <Icons.Plane className="w-6 h-6 text-sky-600 group-hover:text-white" />,
                points: [
                    "Located approximately 33 miles (56 km) south of the resort.",
                    "Direct pre-paid taxis, app-based rides (Uber/Ola), and luxury airport transfers available.",
                    "Route: Take the East Coast Road (ECR). It is a scenic 90-minute coastal drive."
                ]
            }
        ]
    },
    {
        id: "road",
        label: "By Road",
        mainIcon: <Icons.Car className="w-6 h-6" />,
        description: "Scenic drives and local connectivity",
        blocks: [
            {
                title: "From Chengalpattu Junction",
                badge: "Nearest Train Station",
                icon: <Icons.Train className="w-6 h-6 text-indigo-600 group-hover:text-white" />,
                points: [
                    "Distance: Approx 29-30 km away.",
                    "Transport: Taxis and autos available (~₹600).",
                    "Public Transit: Local buses from Chengalpattu terminal take approx 45 minutes."
                ]
            },
            {
                title: "From Chennai Central Railway",
                badge: "Major Hub",
                icon: <Icons.Train className="w-6 h-6 text-indigo-600 group-hover:text-white" />,
                points: [
                    "Distance: Approx 55 km from the resort.",
                    "Best Route: East Coast Road (ECR).",
                    "Time: 90 minutes by car (Tolls apply)."
                ]
            },
            {
                title: "Bus & Public Transport",
                badge: "Budget Friendly",
                icon: <Icons.Bus className="w-6 h-6 text-teal-600 group-hover:text-white" />,
                points: [
                    "Stop: Mahabalipuram Depot (950m walk to resort).",
                    "Connectivity: Frequent AC/Non-AC buses from Koyambedu (CMBT) and Tiruvanmiyur.",
                    "Connectivity: Well linked to Pondicherry and other TN cities via ECR."
                ]
            }
        ]
    }
];

const HowToReach = () => {
    return (
        <section className="relative w-full bg-slate-50 py-12 md:py-24 px-4 md:px-8 font-sans overflow-hidden">
            {/* Background Texture */}
            <div
                className="absolute inset-0 opacity-[0.03] pointer-events-none"
                style={{
                    backgroundImage: `radial-gradient(#475569 1px, transparent 1px)`,
                    backgroundSize: "32px 32px"
                }}
            />

            <div className="relative max-w-6xl mx-auto">
                {/* Header Section */}
                <div className="mb-12 md:mb-20 text-center max-w-3xl mx-auto">
                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm text-slate-600 text-[10px] md:text-[11px] font-bold uppercase tracking-widest mb-4 md:mb-6">
                        <Icons.MapPin className="w-3 h-3 text-sky-500" />
                        Travel Guide
                    </span>
                    {/* Responsive Typography: text-2xl on mobile, text-4xl on desktop */}
                    <h2 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4 md:mb-6 tracking-tight leading-tight">
                        How to Reach Radisson Blu Resort, GRT Temple Bay
                    </h2>
                    <p className="text-slate-500 text-sm md:text-lg leading-relaxed px-2">
                        Radisson Blu Resort, Mamallapuram is well-connected through air and road.
                        Explore the best routes to reach your destination.
                    </p>
                </div>

                {/* Sections */}
                <div className="space-y-12 md:space-y-20">
                    {SECTIONS_DATA.map((section) => (
                        <div key={section.id} className="w-full">
                            {/* Section Heading */}
                            <div className="flex flex-col items-center text-center mb-8 md:mb-12 pb-6 md:pb-8 border-b border-slate-200">
                                <div className="mb-4">
                                    <div className="p-3 rounded-2xl bg-slate-100 shadow-sm">
                                        {section.mainIcon}
                                    </div>
                                </div>
                                <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-2">
                                    {section.label}
                                </h3>
                                <p className="text-sm md:text-xl text-slate-600 font-medium">
                                    {section.description}
                                </p>
                            </div>

                            {/* Grid System */}
                            <div className="flex justify-center">
                                <div
                                    className={
                                        section.id === "air"
                                            ? "w-full md:max-w-3xl"
                                            : "grid gap-6 md:gap-8 grid-cols-1 md:grid-cols-2"
                                    }
                                >
                                    {section.blocks.map((block, bIdx) => (
                                        <div
                                            key={bIdx}
                                            className={`group relative bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-sm border border-slate-200/60 
                        transition-all duration-300 ease-out 
                        hover:shadow-xl hover:-translate-y-1 hover:border-slate-300
                        ${
                                                // Logic: Span 2 columns on desktop for the last item if it's the "road" section
                                                section.id === "road" && section.blocks.length === 3 && bIdx === 2
                                                    ? "md:col-span-2 md:max-w-2xl md:mx-auto"
                                                    : ""
                                                }`}
                                        >
                                            {/* Flex layout: Column on mobile (Icon top), Row on sm/lg (Icon left) */}
                                            <div className="flex flex-col sm:flex-row gap-5 md:gap-6 items-start">

                                                {/* Icon Box */}
                                                <div className="flex-shrink-0">
                                                    <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl border-2 border-slate-100 flex items-center justify-center 
                            bg-slate-50 text-slate-500 shadow-sm
                            group-hover:bg-blue-600 group-hover:border-blue-600 group-hover:text-white 
                            group-hover:scale-105 md:group-hover:scale-110 
                            transition-all duration-500 ease-out">
                                                        {block.icon}
                                                    </div>
                                                </div>

                                                {/* Content */}
                                                <div className="flex-grow min-w-0 w-full">
                                                    <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-3 md:mb-5">
                                                        <h4 className="text-lg md:text-2xl font-bold text-slate-900 group-hover:text-blue-600 transition-colors duration-300">
                                                            {block.title}
                                                        </h4>
                                                        {block.badge && (
                                                            <span className="px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 text-[10px] md:text-xs font-bold uppercase tracking-wider border border-slate-200">
                                                                {block.badge}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <ul className="space-y-3 md:space-y-4">
                                                        {block.points.map((point, pIdx) => (
                                                            <li
                                                                key={pIdx}
                                                                className="flex items-start gap-3 text-slate-600 text-sm md:text-base leading-relaxed"
                                                            >
                                                                <span className="mt-1.5 md:mt-2 h-2 w-2 rounded-full bg-gradient-to-r from-sky-500 to-blue-500 flex-shrink-0" />
                                                                <span className="flex-1">{point}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default HowToReach;